import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TruncateWithTooltip } from '~/modules/settings/components/TruncateWithTooltip';
import type { UserListItem } from '~settings/api/lib/schemas';
import { RelativeDate } from '~settings/components/RelativeDate';
import { UserRole } from '~settings/components/UserRole';
import { UserInfo } from '~settings/modules/user/components/UserInfo';

export const useUsersColumns = (labelId?: string) => {
  const { t } = useTranslation('settings');

  return useMemo(() => {
    const columnHelper = createColumnHelper<UserListItem>();

    const usersColumns = {
      name: columnHelper.accessor((row) => row.fullName, {
        id: 'fullName',
        header: () => t('name'),
        cell: (info) => <UserInfo fullName={info.row.original.fullName} />,
        enableSorting: true,
        size: 180,
      }),
      email: columnHelper.accessor('email', {
        id: 'email',
        header: () => t('email'),
        cell: (cell) => (
          <TruncateWithTooltip className="inline-block">
            {cell.getValue()}
          </TruncateWithTooltip>
        ),
        enableSorting: true,
        size: 180,
      }),
      role: columnHelper.accessor('labels', {
        id: 'role',
        header: () => t('role'),
        cell: (info) => {
          const labels = info.getValue();
          const roles = labels.find(({ id }) => id === labelId)?.roles ?? [];

          return <UserRole roles={roles} />;
        },
        enableSorting: false,
        size: 210,
      }),
      lastLogin: columnHelper.accessor('lastLogin', {
        id: 'lastLogin',
        header: () => t('lastLogin'),
        cell: (info) => <RelativeDate data={info.getValue()} />,
        enableSorting: true,
        size: 195,
      }),
    };

    return Object.values(usersColumns);
  }, [labelId, t]);
};
